import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import usePushNotifications from '../../hooks/usePushNotifications';
import './Sidebar.css';

const Sidebar = ({ 
  chats, 
  currentChatId, 
  onSelectChat, 
  onNewChat, 
  onDeleteChat,
  loading,
  isOpen,
  onClose
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSubscribed, loading: notifLoading, subscribe, unsubscribe } = usePushNotifications();

  const path = location.pathname;
  const isAdmin = user?.is_admin;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  const handleNav = (to) => {
    navigate(to);
    if (onClose) onClose();
  };

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`}>
      {/* New Chat Button */}
      {onNewChat ? (
        <button className="sidebar-new-chat" onClick={onNewChat}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>
          <span>New Chat</span>
        </button>
      ) : (
        <button className="sidebar-new-chat" onClick={() => handleNav('/')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
          <span>Back to Chat</span>
        </button>
      )}

      {/* Admin Navigation */}
      {isAdmin && (
        <div className="sidebar-admin-nav">
          <div className="sidebar-section-title">Admin</div>
          <button
            className={`sidebar-admin-item ${path === '/dashboard' ? 'active' : ''}`}
            onClick={() => handleNav('/dashboard')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            <span>HR Dashboard</span>
          </button>
          <button
            className={`sidebar-admin-item ${path === '/certification/dashboard' ? 'active' : ''}`}
            onClick={() => handleNav('/certification/dashboard')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <span>PSW Certification</span>
          </button>
        </div>
      )}

      {/* Chat List */}
      <div className="sidebar-chats">
        <div className="sidebar-section-title">Recent Chats</div>
        {chats ? (
          loading ? (
            <div className="sidebar-loading">Loading chats...</div>
          ) : chats.length === 0 ? (
            <div className="sidebar-empty">No chats yet</div>
          ) : (
            <div className="sidebar-chat-list">
              {chats.map(chat => (
                <div 
                  key={chat.id}
                  className={`sidebar-chat-item ${currentChatId === chat.id ? 'active' : ''}`}
                  onClick={() => onSelectChat ? onSelectChat(chat.id) : handleNav('/')}
                >
                  <div className="chat-item-icon">
                    {chat.contact_name ? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
                    )}
                  </div>
                  <div className="chat-item-content">
                    <div className="chat-item-title">{chat.title}</div>
                    {chat.contact_name && <div className="chat-item-subtitle">About: {chat.contact_name}</div>}
                    <div className="chat-item-date">{formatDate(chat.updated_at)}</div>
                  </div>
                  {onDeleteChat && (
                    <button className="chat-item-delete" onClick={(e) => { e.stopPropagation(); onDeleteChat(chat.id); }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="sidebar-empty sidebar-empty-hint">
            <button className="sidebar-goto-chat" onClick={() => handleNav('/')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
              Open Chat
            </button>
          </div>
        )}
      </div>

      {/* User Section */}
      <div className="sidebar-user">
        <div className="sidebar-user-info">
          <div className="sidebar-user-avatar">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="sidebar-user-details">
            <div className="sidebar-user-name">{user?.name || 'User'}</div>
            <div className="sidebar-user-email">{user?.email}</div>
          </div>
        </div>
        <div className="sidebar-user-actions">
          <button
            className={`sidebar-notif-btn ${isSubscribed ? 'active' : ''}`}
            onClick={() => isSubscribed ? unsubscribe() : subscribe()}
            disabled={notifLoading}
            title={isSubscribed ? 'Notifications on' : 'Enable notifications'}
          >
            {isSubscribed ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
            )}
          </button>
          <ThemeToggle />
          <button className="sidebar-logout" onClick={logout} title="Logout">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
